// Splitting an assessment into the sections drawn by the progress bar.
//
// The split used to be inferred from exercise priorities: every INTRO started a
// section and the hundreds digit of the priority was its number. That only held
// while authors kept priorities in tidy 100-blocks, and it broke as soon as an
// exercise was inserted or reordered.
//
// The definition now carries its own categories (`GET
// /public/assessment/definition/{id}/category`, ordered by priority) and every
// exercise points at one of them through `assessmentCategoryId`. That is the
// authoritative split, so it is what this module works from.

/**
 * Categories in the order the definition lists them. The API already sorts by
 * priority, this only makes the order deterministic when a priority is missing
 * or shared - those keep their relative order and sink below the numbered ones.
 */
export function orderCategories(categoryList) {
  return (Array.isArray(categoryList) ? categoryList : [])
    .filter((category) => category && category.id != null)
    .map((category, index) => ({ category, index }))
    .sort((a, b) => {
      const priorityA = a.category.priority;
      const priorityB = b.category.priority;
      if (priorityA == null && priorityB == null) return a.index - b.index;
      if (priorityA == null) return 1;
      if (priorityB == null) return -1;
      if (priorityA !== priorityB) return priorityA - priorityB;
      return a.index - b.index;
    })
    .map(({ category }) => category);
}

/**
 * Groups the exercises of a definition into sections.
 *
 * `exerciseList` is expected in delivery order (the API returns it sorted by
 * priority) and the sections stay contiguous blocks of it, because progress is
 * counted as "how many exercises are done" and compared against those blocks.
 *
 * An exercise without a category joins the section that is already running -
 * a question whose category was never filled in belongs to the part the
 * candidate is in the middle of, not to a section of its own. Uncategorised
 * exercises ahead of the first categorised one form a leading, unnamed section.
 *
 * Returns `[{ name, sectionNum, categoryId, exercises }]`.
 */
export function buildCategorySections(exerciseList, categoryList) {
  const exercises = Array.isArray(exerciseList) ? exerciseList : [];
  const categories = orderCategories(categoryList);

  // Section numbers come from the definition's own category order, so they stay
  // put when a category happens to hold no exercises.
  const byId = new Map(
    categories.map((category, index) => [category.id, { name: category.name, sectionNum: index + 1 }]),
  );

  const sections = [];
  let current = null;

  for (const exercise of exercises) {
    const categoryId = exercise?.assessmentCategoryId ?? null;

    if (categoryId === null) {
      // No category of its own: continue the running section, or open the
      // leading unnamed one when nothing has started yet.
      if (current === null) {
        current = { name: null, sectionNum: null, categoryId: null, exercises: [] };
        sections.push(current);
      }
      current.exercises.push(exercise);
      continue;
    }

    if (current === null || current.categoryId !== categoryId) {
      const known = byId.get(categoryId);
      current = {
        name: known?.name ?? exercise?.assessmentCategoryName ?? null,
        sectionNum: known?.sectionNum ?? sections.length + 1,
        categoryId,
        exercises: [],
      };
      sections.push(current);
    }

    current.exercises.push(exercise);
  }

  return sections;
}

/**
 * The pre-category split, kept as a fallback for definitions whose categories
 * have not been filled in yet: every INTRO opens a section and the hundreds
 * digit of the priority numbers it. Without it such an assessment would draw a
 * single dot for the whole run.
 */
export function buildIntroSections(exerciseList) {
  const exercises = Array.isArray(exerciseList) ? exerciseList : [];
  const sections = [];
  let current = null;

  for (const exercise of exercises) {
    if (exercise?.exerciseType === "INTRO" || current === null) {
      current = {
        name: exercise?.exerciseType === "INTRO" ? (exercise?.name ?? exercise?.title ?? null) : null,
        sectionNum:
          exercise?.exerciseType === "INTRO" && exercise?.priority != null
            ? Math.floor(exercise.priority / 100)
            : sections.length + 1,
        categoryId: null,
        exercises: [],
      };
      sections.push(current);
    }
    current.exercises.push(exercise);
  }

  return sections;
}

/**
 * The split the progress bar should draw: categories when the definition has
 * them, the INTRO fallback while it has none.
 */
export function buildExerciseSections(exerciseList, categoryList) {
  const exercises = Array.isArray(exerciseList) ? exerciseList : [];
  const hasCategorisedExercise = exercises.some((exercise) => exercise?.assessmentCategoryId != null);

  return hasCategorisedExercise
    ? buildCategorySections(exercises, categoryList)
    : buildIntroSections(exercises);
}

/**
 * "done" / "active" / "pending" per section, from the count of completed
 * exercises. A section is done once every exercise in it is, and the one the
 * candidate is in stays active from its first page onward.
 */
export function computeSectionStates(sections, doneCount) {
  const done = Number.isFinite(doneCount) ? doneCount : 0;
  let cumulative = 0;

  return (Array.isArray(sections) ? sections : []).map((section) => {
    const sectionEnd = cumulative + (section?.exercises?.length || 0);
    const isDone = done >= sectionEnd;
    const isActive = !isDone && done >= cumulative;
    cumulative = sectionEnd;
    return isDone ? "done" : isActive ? "active" : "pending";
  });
}

/**
 * Total number of exercises across every section - the point at which the whole
 * assessment is finished.
 */
export function countExercises(sections) {
  return (Array.isArray(sections) ? sections : []).reduce(
    (total, section) => total + (section?.exercises?.length || 0),
    0,
  );
}

/**
 * Fill percentage of the connector leading *out of* each section, so there is
 * one segment per part rather than one per gap between parts.
 *
 * The bar draws a closing dot after the last section, which is what makes that
 * possible: with N parts there are N + 1 dots and N segments, and every part
 * owns exactly one of them. Indexing the segments the other way round - into
 * each section rather than out of it - left the first part without a segment
 * and made the bar read as one part behind.
 */
export function computeSectionFills(sections, doneCount) {
  const list = Array.isArray(sections) ? sections : [];
  const done = Number.isFinite(doneCount) ? doneCount : 0;
  const fills = [];
  let cumulative = 0;

  for (const section of list) {
    const length = section?.exercises?.length || 0;
    // A section with no exercises still owns a segment; treat it as length one
    // so it fills in one step instead of dividing by zero.
    const divisor = Math.max(length, 1);
    const doneInSection = Math.min(Math.max(done - cumulative, 0), divisor);

    fills.push((doneInSection / divisor) * 100);
    cumulative += length;
  }

  return fills;
}

/**
 * State of the closing dot: "done" once every exercise is, "pending" until
 * then. It never goes "active" - there is nothing to be in the middle of.
 */
export function computeFinishState(sections, doneCount) {
  const total = countExercises(sections);
  const done = Number.isFinite(doneCount) ? doneCount : 0;

  return total > 0 && done >= total ? "done" : "pending";
}
